// 중단 — 당일 Top3 vs Smoothed Top3 격차 패널 (P1, O-DB-7 결착됨).
// 권위: ARCH §6.2 + PRD §5.7.2 + QUANT §2.5.5.
// 본 화면은 단일 시점 스냅샷 (시계열 아님).

import { memo, useMemo } from 'react';
import type { DivergencePanelResponse, TopSectorEntry } from './leadingSectors.types';

interface Props {
  data: DivergencePanelResponse | undefined;
  isLoading?: boolean;
  /** 백엔드 에러 (404 / 503 등). */
  error?: { code: string; message: string; httpStatus: number } | null;
  date: string;
}

export const DivergencePanel = memo(function DivergencePanel({
  data,
  isLoading,
  error,
  date,
}: Props): JSX.Element {
  // 교집합 sector_name 집합. ARCH §6.2 응답 intersection_size 와 정합 검증용.
  const intersect = useMemo(() => {
    if (!data) return new Set<string>();
    const dailySet = new Set(data.daily_top3.map((r) => r.sector_name));
    const out = new Set<string>();
    for (const r of data.smoothed_top3) {
      if (dailySet.has(r.sector_name)) out.add(r.sector_name);
    }
    return out;
  }, [data]);

  // 에러 상태 분기.
  if (error) {
    if (error.httpStatus === 404) {
      return (
        <div className="ls-divergence">
          <div className="ls-empty">
            {date} 일자 격차 데이터 없음 (휴장 또는 미적재).
          </div>
        </div>
      );
    }
    if (error.httpStatus === 503) {
      return (
        <div className="ls-divergence">
          <div className="ls-banner ls-banner--warn">
            격차 산식 결착 대기 중 (O-DB-7). 패널은 결착 후 활성화됩니다.
          </div>
        </div>
      );
    }
    return (
      <div className="ls-divergence">
        <div className="ls-banner ls-banner--error">
          격차 패널 로드 실패: {error.message}
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="ls-divergence">
        <div className="ls-empty">불러오는 중…</div>
      </div>
    );
  }

  const state = data.threshold_state;
  const stateLabel: Record<string, string> = {
    NORMAL: 'NORMAL — 격차 정상',
    WATCH: 'WATCH — 격차 주의',
    ALERT: 'ALERT — 레짐 전환 후보',
  };
  const persistSuffix =
    data.persistence_count >= 2 ? ` · 최근 ${data.persistence_count}거래일 연속 WATCH+` : '';

  return (
    <div className="ls-divergence" data-testid="ls-divergence">
      <div
        className={`ls-divergence__state-banner ls-divergence__state-banner--${state}`}
        role="status"
      >
        <strong data-testid="ls-divergence-state">{stateLabel[state] ?? state}{persistSuffix}</strong>
        <span className="ls-divergence__metrics">
          <span>k = {data.intersection_size} / 3</span>
          <span>Jaccard = {fmt(data.divergence_jaccard, 2)}</span>
          <span>DivScore = {fmt(data.divergence_score, 2)}</span>
        </span>
      </div>

      <div className="ls-divergence__columns">
        <Top3Column title="당일 Top3 (Daily)" rows={data.daily_top3} intersect={intersect} />
        <Top3Column title="60일 Smoothed Top3" rows={data.smoothed_top3} intersect={intersect} />
      </div>

      <div className="ls-muted" style={{ fontSize: 'var(--text-xs)' }}>
        기준일 {data.date}
        {data.pipeline_mode ? ` · mode=${data.pipeline_mode}` : ''}
        {data.run_id ? ` · run ${data.run_id.slice(0, 8)}` : ''}
      </div>
    </div>
  );
});

function Top3Column({
  title,
  rows,
  intersect,
}: {
  title: string;
  rows: TopSectorEntry[];
  intersect: Set<string>;
}): JSX.Element {
  return (
    <div className="ls-top3">
      <h3 className="ls-top3__title">{title}</h3>
      {rows.length === 0 && <div className="ls-muted">데이터 없음</div>}
      {rows.map((r) => {
        const hit = intersect.has(r.sector_name);
        return (
          <div
            key={`${title}-${r.rank}-${r.sector_name}`}
            className={`ls-top3__row${hit ? ' ls-top3__row--intersect' : ''}`}
          >
            <span className="ls-top3__rank">#{r.rank}</span>
            <span className="ls-top3__name">
              {r.sector_name}
              {hit && <span className="ls-top3__intersect-mark">∩</span>}
            </span>
            <span className="ls-top3__score">{fmt(r.score_daily, 2)}</span>
          </div>
        );
      })}
    </div>
  );
}

function fmt(n: number | null | undefined, digits: number): string {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toFixed(digits);
}
